import localforage from 'localforage';
import SDK from '../Components/SDK';
import {
    getFromLocalStorageAndDecrypt,
    encryptAndStoreInLocalStorage,
} from '../Components/WebChat/WebChatEncryptDecrypt';
import { REACT_APP_API_URL } from '../Components/processENV';
import { MediaDownloadDataAction } from '../Actions/Media';
import { Store } from '../store';
import logger from '../Helpers/logger';
import { clearKeyValueStores } from '../services/keyValueStore';
import { CHAT_AUDIOS, CHAT_IMAGES, CHAT_VIDEOS, STORY_MEDIA, INVALID_STORENAME } from './Constants';
import configureRefreshFetch from './refreshFetch';

const shouldRefreshToken = error => {
    return error.status === 401 && error.message === 'Token Expired';
};
const mediaUrl = `${REACT_APP_API_URL}/media/`;
const retrieveToken = () => getFromLocalStorageAndDecrypt('token');

const refreshToken = () => {
    let decryptResponse = getFromLocalStorageAndDecrypt('auth_user');
    return SDK.getUserToken(decryptResponse.username, decryptResponse.password)
        .then(async resp => {
            if (resp.statusCode === 200) {
                SDK.setUserToken(resp.userToken);
                encryptAndStoreInLocalStorage('token', resp.userToken);
            }
        })
        .catch(error => {
            logger.debug('handleLogin error, ', error);
        });
};

const refreshFetch = configureRefreshFetch({
    shouldRefreshToken,
    refreshToken,
    fetch: fetch,
});

const substringBetween = (s, a, b) => {
    if (s.indexOf(b) === -1) {
        return s;
    }
    let p = s.indexOf(a) + a.length;
    return s.substring(p, s.indexOf(b, p));
};

class IndexedDb {
    constructor() {
        if (IndexedDb.instance) {
            return IndexedDb.instance; //NOSONAR
        }
        this._localforage = localforage;
        const dbName = 'mirrorflydb';
        this._localforage.profileimages = localforage.createInstance({
            name: dbName,
            storeName: 'profileimages',
            description: 'profile image store at offline',
        });
        this._localforage[CHAT_IMAGES] = localforage.createInstance({
            name: dbName,
            storeName: CHAT_IMAGES,
            description: 'chat image store at offline',
        });
        this._localforage[CHAT_AUDIOS] = localforage.createInstance({
            name: dbName,
            storeName: CHAT_AUDIOS,
            description: 'chat audio store at offline',
        });
        this._localforage[CHAT_VIDEOS] = localforage.createInstance({
            name: dbName,
            storeName: CHAT_VIDEOS,
            description: 'chat video store at offline',
        });
        this._localforage[STORY_MEDIA] = localforage.createInstance({
            name: dbName,
            storeName: STORY_MEDIA,
            description: 'story media store at offline',
        });
        this._localforage.calllogs = localforage.createInstance({
            name: dbName,
            storeName: 'callLogs',
            description: 'maintaining the call logs',
        });
        this._localforage.groupMembers = localforage.createInstance({
            name: dbName,
            storeName: 'groupMembers',
            description: 'maintaining the groupMembers',
        });
        this._localforage.recentChats = localforage.createInstance({
            name: dbName,
            storeName: 'recentChats',
            description: 'recent chat list cache',
        });
        this._localforage.chatMessages = localforage.createInstance({
            name: dbName,
            storeName: 'chatMessages',
            description: 'chat message history cache per chatId',
        });
        this._localforage.groupsList = localforage.createInstance({
            name: dbName,
            storeName: 'groupsList',
            description: 'groups list cache',
        });
        this._localforage.cacheMetadata = localforage.createInstance({
            name: dbName,
            storeName: 'cacheMetadata',
            description: 'IDB cache metadata and TTL tracking',
        });
        IndexedDb.instance = this;
    }

    checkStatus(response) {
        if (response.status >= 200 && response.status < 300) {
            return response;
        }
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
    }

    parseBlob(response) {
        if (response.status === 204 || response.status === 205) {
            return null;
        }
        return response.blob();
    }

    setImage(key, blobInstance, storeName) {
        if (!storeName) {
            throw new Error(INVALID_STORENAME);
        }
        return this._localforage[storeName].setItem(key, blobInstance);
    }

    removeImage(key, storeName) {
        if (!storeName) {
            throw new Error(INVALID_STORENAME);
        }
        return this._localforage[storeName].removeItem(key);
    }

    async removeAllItems(storeName) {
        let keys = await this._localforage[storeName].keys();
        let allImages = [];
        for (let key of keys) {
            allImages.push(await this.removeImage(key, storeName));
        }
        return allImages;
    }

    async makeHttpRequest(key, storeName, fileKey, msgId = '') {
        return new Promise((resolve, reject) => {
            const uniqueKey = substringBetween(key, mediaUrl, '?mf');
            if (msgId) {
                SDK.downloadMedia(
                    msgId,
                    res => {
                        Store.dispatch(MediaDownloadDataAction(res));
                    },
                    response => {
                        return this.setImage(uniqueKey, response.blob, storeName).then(() => {
                            Store.dispatch(MediaDownloadDataAction({ msgId, progress: null }));
                            resolve(this.getImageByKey(uniqueKey, storeName, fileKey, msgId));
                        });
                    },
                    error => {
                        logger.debug('error occured in downloading media', error);
                    },
                );
            } else {
                SDK.getMediaURL(uniqueKey, fileKey, msgId)
                    .then(response => {
                        if (response.statusCode === 200) {
                            return this.setImage(uniqueKey, response.data.blob, storeName).then(() => {
                                resolve(this.getImageByKey(uniqueKey, storeName, fileKey, msgId));
                            });
                        }
                        reject(response);
                    })
                    .catch(error => {
                        logger.debug('Error in getMediaURL', error);
                        reject(error);
                    });
            }
        });
    }

    // Check if image exists in IndexedDB without triggering download
    checkImageExists(keyString, storeName) {
        if (!keyString) {
            return Promise.reject(null);
        }
        if (!storeName) {
            throw new Error(INVALID_STORENAME);
        }
        return this._localforage[storeName]?.getItem(keyString).then(blob => {
            // Return the blob if exists, null if not (without triggering download)
            return blob;
        });
    }

    // Check if video exists in IndexedDB without triggering download
    checkVideoExists(keyString) {
        if (!keyString) {
            return Promise.reject(null);
        }
        return this._localforage[CHAT_VIDEOS]?.getItem(keyString)
            .then(blob => {
                // Return the blob if exists, null if not (without triggering download)
                return blob;
            })
            .catch(error => {
                logger.error('Error checking video in IndexedDB:', error);
                return null;
            });
    }

    getImageByKey(keyString, storeName, fileKey, msgId = '') {
        if (!keyString) {
            return Promise.reject(null);
        }
        if (!storeName) {
            throw new Error(INVALID_STORENAME);
        }
        return this._localforage[storeName]?.getItem(keyString).then(async blob => {
            if (!blob) {
                let result = await this.makeHttpRequest(keyString, storeName, fileKey, msgId);
                return result;
            }
            return blob;
        });
    }

    // Get video by key - checks IndexedDB first, then downloads via SDK if needed
    getVideoByKey(keyString, fileKey, msgId = '') {
        if (!keyString) {
            return Promise.reject(null);
        }
        return this._localforage[CHAT_VIDEOS]?.getItem(keyString)
            .then(async blob => {
                if (!blob) {
                    // Video not in IndexedDB, download via SDK
                    let result = await this.makeHttpRequest(keyString, CHAT_VIDEOS, fileKey, msgId);
                    return result;
                }
                return blob;
            })
            .catch(error => {
                logger.error('Error getting video from IndexedDB:', error);
                return Promise.reject(error);
            });
    }

    setStoryMedia(key, blob) {
        return this._localforage[STORY_MEDIA].setItem(key, blob);
    }

    getStoryMedia(key) {
        if (!key) return Promise.resolve(null);
        return this._localforage[STORY_MEDIA].getItem(key);
    }

    checkStoryMediaExists(key) {
        if (!key) return Promise.resolve(null);
        return this._localforage[STORY_MEDIA].getItem(key);
    }

    async getCallLogs() {
        let callLogs = [];
        return await localforage.calllogs
            .length()
            .then(async function (numberOfKeys) {
                if (numberOfKeys > 0) {
                    return localforage.calllogs
                        .iterate(function (value, _key, _iterationNumber) {
                            callLogs.push(value);
                        })
                        .then(function () {
                            return Promise.resolve(callLogs);
                        })
                        .catch(function (err) {
                            logger.debug(err);
                            return Promise.resolve(callLogs);
                        });
                } else {
                    return Promise.resolve(callLogs);
                }
            })
            .catch(async function (_err) {
                return Promise.resolve(callLogs);
            });
    }

    async insertCallLogs(callLogs) {
        callLogs.map(callLog => {
            return this._localforage.calllogs.setItem(callLog.roomId, callLog);
        });
    }

    getCallLogByRoomId(roomId) {
        if (!roomId) {
            return Promise.resolve(null);
        }
        return localforage.calllogs.getItem(roomId).then(callLog => {
            return callLog;
        });
    }

    async insertGroupMemberDetails(groupId, participants) {
        return this._localforage.groupMembers.setItem(groupId, participants);
    }

    getGroupMembersByGroupId(groupId) {
        if (!groupId) {
            return Promise.resolve(null);
        }
        return localforage.groupMembers.getItem(groupId).then(participants => {
            return participants;
        });
    }

    // ─── Recent Chats Cache ───────────────────────────────────────────────────

    async saveRecentChats(chats) {
        if (!Array.isArray(chats) || !chats.length) return;
        try {
            for (const chat of chats) {
                if (chat?.fromUserId) {
                    await this._localforage.recentChats.setItem(chat.fromUserId, chat);
                }
            }
        } catch (e) {
            logger.warn('[IDB] saveRecentChats failed', e);
        }
    }

    async getRecentChats() {
        const chats = [];
        try {
            await this._localforage.recentChats.iterate(value => {
                chats.push(value);
            });
            return chats.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        } catch (e) {
            logger.warn('[IDB] getRecentChats failed', e);
            return [];
        }
    }

    async upsertRecentChat(chat) {
        if (!chat?.fromUserId) return;
        try {
            const existing = (await this._localforage.recentChats.getItem(chat.fromUserId)) || {};
            await this._localforage.recentChats.setItem(chat.fromUserId, { ...existing, ...chat });
        } catch (e) {
            logger.warn('[IDB] upsertRecentChat failed', e);
        }
    }

    async removeRecentChat(chatId) {
        if (!chatId) return;
        try {
            const normalized = chatId.includes('@') ? chatId.split('@')[0] : chatId;
            await this._localforage.recentChats.removeItem(normalized);
        } catch (e) {
            logger.warn('[IDB] removeRecentChat failed', e);
        }
    }

    // ─── Chat Messages Cache ──────────────────────────────────────────────────

    async saveMessages(chatId, messages) {
        if (!chatId || !messages) return;
        try {
            const incoming = Array.isArray(messages)
                ? messages.reduce((acc, m) => {
                      if (m?.msgId) acc[m.msgId] = m;
                      return acc;
                  }, {})
                : messages;
            const existing = await this._localforage.chatMessages.getItem(chatId);
            const merged = { ...(existing?.messages || {}), ...incoming };
            // Keep only the most recent 100 messages per chat to bound storage usage
            const sorted = Object.values(merged).sort((a, b) => (a.msgId > b.msgId ? 1 : -1));
            const limited = sorted.slice(-100);
            const limitedObj = limited.reduce((acc, m) => {
                acc[m.msgId] = m;
                return acc;
            }, {});
            await this._localforage.chatMessages.setItem(chatId, { messages: limitedObj, lastUpdated: Date.now() });
        } catch (e) {
            logger.warn('[IDB] saveMessages failed', e);
        }
    }

    async getMessages(chatId) {
        if (!chatId) return null;
        try {
            return await this._localforage.chatMessages.getItem(chatId);
        } catch (e) {
            logger.warn('[IDB] getMessages failed', e);
            return null;
        }
    }

    async deleteMessage(chatId, msgId) {
        if (!chatId || !msgId) return;
        try {
            const existing = await this._localforage.chatMessages.getItem(chatId);
            if (!existing?.messages?.[msgId]) return;
            const { [msgId]: _removed, ...remaining } = existing.messages;
            await this._localforage.chatMessages.setItem(chatId, { messages: remaining, lastUpdated: Date.now() });
        } catch (e) {
            logger.warn('[IDB] deleteMessage failed', e);
        }
    }

    async deleteMessagesByChatId(chatId) {
        if (!chatId) return;
        try {
            await this._localforage.chatMessages.removeItem(chatId);
        } catch (e) {
            logger.warn('[IDB] deleteMessagesByChatId failed', e);
        }
    }

    // ─── Groups List Cache ────────────────────────────────────────────────────

    async saveGroups(groups) {
        if (!Array.isArray(groups) || !groups.length) return;
        try {
            for (const group of groups) {
                if (group?.groupId) {
                    await this._localforage.groupsList.setItem(group.groupId, group);
                }
            }
        } catch (e) {
            logger.warn('[IDB] saveGroups failed', e);
        }
    }

    async getGroups() {
        const groups = [];
        try {
            await this._localforage.groupsList.iterate(value => {
                groups.push(value);
            });
            return groups;
        } catch (e) {
            logger.warn('[IDB] getGroups failed', e);
            return [];
        }
    }

    // ─── Cache Metadata / TTL ─────────────────────────────────────────────────

    async getMetadata(key) {
        try {
            return await this._localforage.cacheMetadata.getItem(key);
        } catch (e) {
            return null;
        }
    }

    async setMetadata(key, data) {
        try {
            await this._localforage.cacheMetadata.setItem(key, { ...data, savedAt: Date.now() });
        } catch (e) {
            logger.warn('[IDB] setMetadata failed', e);
        }
    }

    // ─── Full Clear (logout) ──────────────────────────────────────────────────

    async clearChatData() {
        try {
            await Promise.all([
                this._localforage.recentChats.clear(),
                this._localforage.chatMessages.clear(),
                this._localforage.groupsList.clear(),
                this._localforage.cacheMetadata.clear(),
                clearKeyValueStores(),
            ]);
        } catch (e) {
            logger.warn('[IDB] clearChatData failed', e);
        }
    }
}

export default IndexedDb;
